import { useState, useEffect, useContext } from 'react';
import api from '../services/api';
import { 
  Search, 
  Plus, 
  UserPlus, 
  Mail, 
  Phone, 
  Award, 
  X, 
  Save, 
  Edit2, 
  Trash2, 
  Users, 
  Wallet, 
  History, 
  ArrowUpRight, 
  ArrowDownRight,
  ChevronLeft,
  ChevronRight,
  CheckSquare,
  Square
} from 'lucide-react';
import { AuthContext } from '../contexts/AuthContext';

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedIds, setSelectedIds] = useState(new Set());
  
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPayModalOpen, setIsPayModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  
  const [currentCustomer, setCurrentCustomer] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', creditLimit: 0 });
  const [payData, setPayData] = useState({ amount: '', description: 'Account Payment' });
  const [history, setHistory] = useState([]);

  const loadCustomers = () => {
    api.get('/customers').then(res => setCustomers(res.data)).catch(console.error);
  };

  useEffect(() => { loadCustomers(); }, []);

  const openModal = (customer = null) => {
    setCurrentCustomer(customer);
    if (customer) {
      setFormData({ 
        name: customer.name, 
        email: customer.email || '', 
        phone: customer.phone || '',
        creditLimit: customer.creditLimit || 0
      });
    } else {
      setFormData({ name: '', email: '', phone: '', creditLimit: 0 });
    }
    setIsModalOpen(true);
  };

  const openPayModal = (customer) => {
    setCurrentCustomer(customer);
    setPayData({ amount: Math.abs(customer.balance || 0).toFixed(2), description: 'Account Payment' });
    setIsPayModalOpen(true);
  };

  const openHistoryModal = async (customer) => {
    setCurrentCustomer(customer);
    try {
      const res = await api.get(`/customers/${customer.id}`);
      setHistory(res.data.transactions || []);
      setIsHistoryModalOpen(true);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (currentCustomer) await api.put(`/customers/${currentCustomer.id}`, formData);
      else await api.post('/customers', formData);
      setIsModalOpen(false);
      loadCustomers();
    } catch (err) {
      alert('Error saving customer');
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/customers/${currentCustomer.id}/transaction`, {
        type: 'PAYMENT',
        amount: parseFloat(payData.amount),
        description: payData.description
      });
      setIsPayModalOpen(false);
      loadCustomers();
    } catch (err) {
      alert('Error processing payment');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    try {
      await api.delete(`/customers/${id}`);
      setCustomers(prev => prev.filter(c => c.id !== id));
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (err) {
      alert('Error deleting customer');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.size} customers?`)) return;
    const idsToDelete = Array.from(selectedIds);
    try {
      await api.post('/customers/bulk-delete', { ids: idsToDelete });
      setCustomers(prev => prev.filter(c => !selectedIds.has(c.id)));
      setSelectedIds(new Set());
    } catch (err) {
      alert('Error deleting customers');
    }
  };

  const filteredCustomers = customers.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    (c.email && c.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Pagination Logic
  const totalItems = filteredCustomers.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCustomers = filteredCustomers.slice(indexOfFirstItem, indexOfLastItem);

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) newSelected.delete(id);
    else newSelected.add(id);
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === currentCustomers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(currentCustomers.map(c => c.id)));
    }
  };

  return (
    <div className="space-y-6 animate-fade-in-up max-w-7xl mx-auto pb-10">
      <div className="flex justify-between items-end mb-6">
        <div>
          <h2 className="text-3xl font-black text-theme-main uppercase tracking-tight">Customer Hub</h2>
          <p className="text-theme-muted mt-1 font-bold uppercase tracking-widest text-[10px]">Manage loyalty, credit limits, and payment history.</p>
        </div>
        <div className="flex gap-3">
          {isAdmin && selectedIds.size > 0 && (
            <button 
              onClick={handleBulkDelete}
              className="bg-rose-500 hover:bg-rose-600 text-white px-5 py-3 rounded-2xl shadow-lg font-black text-xs uppercase tracking-widest flex items-center gap-2 transition-all active:scale-95 animate-bounce-in"
            >
              <Trash2 size={18} /> Delete Selected ({selectedIds.size})
            </button>
          )}
          <button onClick={() => openModal()} className="bg-brand hover:bg-brand-light text-white px-6 py-3 rounded-2xl shadow-xl font-black text-xs uppercase tracking-widest flex items-center gap-2 transition-all hover:-translate-y-0.5">
            <UserPlus size={18} /> Add New Client
          </button>
        </div>
      </div>

      <div className="bg-theme-card rounded-3xl shadow-soft border border-theme overflow-hidden">
        <div className="p-5 border-b border-theme flex justify-between items-center bg-theme-main/10">
          <div className="relative w-96">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-theme-muted" />
            </div>
            <input
              type="text"
              className="block w-full pl-12 sm:text-sm rounded-2xl py-3 bg-theme-main border border-theme text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all font-bold placeholder:text-theme-muted"
              placeholder="Search clients..."
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            />
          </div>
          <div className="flex items-center gap-4 text-[10px] font-black text-theme-muted uppercase tracking-[0.2em]">
            <Users size={16} /> {filteredCustomers.length} Accounts
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-theme-main/20 text-theme-muted text-[10px] uppercase tracking-widest font-black border-b border-theme">
                {isAdmin && (
                  <th className="pl-6 py-5 w-10">
                    <button onClick={toggleSelectAll} className="text-brand">
                      {selectedIds.size === currentCustomers.length && currentCustomers.length > 0 ? <CheckSquare size={20} /> : <Square size={20} />}
                    </button>
                  </th>
                )}
                <th className={isAdmin ? 'px-4 py-5' : 'px-6 py-5'}>Client Profile</th>
                <th className="px-6 py-5 text-center">Tier</th>
                <th className="px-6 py-5 text-right">Balance</th>
                <th className="px-6 py-5 text-center">Loyalty</th>
                <th className="px-8 py-5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-theme">
              {currentCustomers.map(c => (
                <tr key={c.id} className={`hover:bg-theme-main/10 transition-colors group ${selectedIds.has(c.id) ? 'bg-brand/5' : ''}`}>
                  {isAdmin && (
                    <td className="pl-6 py-5">
                      <button onClick={() => toggleSelect(c.id)} className={selectedIds.has(c.id) ? 'text-brand' : 'text-theme-muted opacity-30 hover:opacity-100'}>
                        {selectedIds.has(c.id) ? <CheckSquare size={20} /> : <Square size={20} />}
                      </button>
                    </td>
                  )}
                  <td className={isAdmin ? 'px-4 py-4' : 'px-6 py-4'}>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-2xl bg-theme-main flex items-center justify-center text-brand font-black border border-theme shadow-inner text-lg">
                        {c.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-black text-theme-main uppercase tracking-tight">{c.name}</div>
                        <div className="text-[10px] text-theme-muted font-bold mt-1 flex items-center gap-3">
                          <span className="flex items-center gap-1"><Mail size={10} /> {c.email || 'N/A'}</span>
                          <span className="flex items-center gap-1"><Phone size={10} /> {c.phone || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col items-center gap-1.5">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest border ${
                        c.tier === 'Gold' ? 'bg-amber-500/10 text-amber-600 border-amber-500/20' :
                        c.tier === 'Silver' ? 'bg-indigo-500/10 text-indigo-500 border-indigo-500/20' :
                        'bg-theme-main text-theme-muted border-theme'
                      }`}>
                        <Award size={12} /> {c.tier}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="text-sm font-black ${c.balance < 0 ? 'text-rose-500' : 'text-emerald-500'}">
                      {c.balance < 0 ? `-$${Math.abs(c.balance).toFixed(2)}` : `$${c.balance.toFixed(2)}`}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="text-sm font-black text-brand">{c.points} pts</div>
                  </td>
                  <td className="px-8 py-4 text-right">
                    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => openHistoryModal(c)} title="View Ledger" className="p-2 text-theme-muted hover:text-brand hover:bg-theme-main rounded-xl transition-all">
                        <History size={18} />
                      </button>
                      {c.balance < 0 && (
                        <button onClick={() => openPayModal(c)} title="Settle Debt" className="p-2 text-emerald-500 hover:bg-emerald-500 hover:text-white rounded-xl transition-all">
                          <Wallet size={18} />
                        </button>
                      )}
                      <button onClick={() => openModal(c)} className="p-2 text-theme-muted hover:text-theme-main hover:bg-theme-main rounded-xl transition-all">
                        <Edit2 size={18} />
                      </button>
                      {isAdmin && (
                        <button onClick={() => handleDelete(c.id)} className="p-2 text-theme-muted hover:text-rose-500 hover:bg-rose-500/10 rounded-xl transition-all">
                          <Trash2 size={18} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-8 py-6 bg-theme-main/5 border-t border-theme flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs font-bold text-theme-muted uppercase tracking-widest">
              Showing <span className="text-theme-main">{indexOfFirstItem + 1}</span> to <span className="text-theme-main">{Math.min(indexOfLastItem, totalItems)}</span> of <span className="text-theme-main">{totalItems}</span> clients
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="p-2 rounded-xl bg-theme-card border border-theme text-theme-muted disabled:opacity-20">
                <ChevronLeft size={20} />
              </button>
              {(() => {
                const pages = [];
                let start = Math.max(1, currentPage - 1);
                let end = Math.min(totalPages, start + 2);
                if (end - start < 2) start = Math.max(1, end - 2);
                for (let i = start; i <= end; i++) pages.push(i);
                return pages.map(p => (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`w-10 h-10 rounded-xl font-black text-xs transition-all ${
                      currentPage === p 
                        ? 'bg-brand text-white shadow-lg shadow-brand/30' 
                        : 'bg-theme-card border border-theme text-theme-muted hover:border-brand'
                    }`}
                  >
                    {p}
                  </button>
                ));
              })()}
              <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className="p-2 rounded-xl bg-theme-card border border-theme text-theme-muted disabled:opacity-20">
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals remain the same */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in p-4">
          <div className="bg-theme-card rounded-3xl shadow-2xl w-full max-w-md overflow-hidden border border-theme">
            <div className="px-8 py-6 border-b border-theme bg-theme-main/10 flex justify-between items-center">
              <h3 className="text-xl font-black text-theme-main uppercase tracking-tight">Account Profile</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-theme-muted hover:text-theme-main p-1.5 rounded-xl transition-colors"><X size={24} /></button>
            </div>
            <form onSubmit={handleSave} className="p-8 space-y-5">
              <div>
                <label className="block text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] mb-2">Client Name</label>
                <input required type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full bg-theme-main border border-theme rounded-2xl py-3 px-5 text-theme-main focus:ring-2 focus:ring-brand outline-none transition-all font-bold" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] mb-2">Email</label>
                  <input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full bg-theme-main border border-theme rounded-2xl py-3 px-5 text-theme-main text-xs focus:ring-2 focus:ring-brand outline-none" />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] mb-2">Phone</label>
                  <input type="tel" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="w-full bg-theme-main border border-theme rounded-2xl py-3 px-5 text-theme-main text-xs focus:ring-2 focus:ring-brand outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] mb-2">Credit Limit ($)</label>
                <input type="number" value={formData.creditLimit} onChange={e => setFormData({...formData, creditLimit: parseFloat(e.target.value)})} className="w-full bg-theme-main border border-theme rounded-2xl py-3 px-5 text-theme-main font-black focus:ring-2 focus:ring-brand outline-none" />
              </div>
              <div className="pt-6 flex gap-3">
                <button type="submit" className="flex-1 px-4 py-4 bg-brand hover:bg-brand-light text-white rounded-2xl font-black transition-all shadow-xl flex items-center justify-center gap-2 uppercase text-xs tracking-widest"><Save size={18} /> Sync Account</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isPayModalOpen && (
        <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-theme-card rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden border border-theme">
            <div className="px-8 py-6 border-b border-theme bg-emerald-500/10 flex items-center gap-3"><Wallet className="text-emerald-500" size={24} /><h3 className="text-xl font-black text-theme-main uppercase tracking-tight">Settle Balance</h3></div>
            <form onSubmit={handlePayment} className="p-8 space-y-6">
              <div className="text-center p-4 bg-theme-main rounded-2xl border border-theme">
                <p className="text-[10px] font-black text-theme-muted uppercase tracking-widest mb-1">Current Debt</p>
                <p className="text-3xl font-black text-rose-500">${Math.abs(currentCustomer.balance).toFixed(2)}</p>
              </div>
              <div>
                <label className="block text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] mb-2">Payment Amount</label>
                <input required type="number" step="0.01" value={payData.amount} onChange={e => setPayData({...payData, amount: e.target.value})} className="w-full bg-theme-main border border-theme rounded-2xl py-4 px-6 text-2xl font-black text-emerald-500 text-center outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setIsPayModalOpen(false)} className="flex-1 py-4 text-theme-muted font-black uppercase text-[10px] tracking-widest">Cancel</button>
                <button type="submit" className="flex-1 py-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-lg shadow-emerald-500/20">Record Payment</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isHistoryModalOpen && (
        <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-theme-card rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden border border-theme flex flex-col max-h-[80vh]">
            <div className="px-8 py-6 border-b border-theme bg-theme-main/10 flex justify-between items-center">
              <div><h3 className="text-xl font-black text-theme-main uppercase tracking-tight">Account Ledger</h3><p className="text-[10px] text-theme-muted font-black uppercase mt-1 tracking-widest">{currentCustomer.name}</p></div>
              <button onClick={() => setIsHistoryModalOpen(false)} className="text-theme-muted hover:text-theme-main p-1.5 rounded-xl transition-colors"><X size={24} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar bg-theme-main/5">
              {history.map(tx => (
                <div key={tx.id} className="bg-theme-card p-4 rounded-2xl border border-theme flex items-center justify-between group hover:border-brand transition-all">
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-xl ${tx.type === 'PAYMENT' ? 'bg-emerald-500/10 text-emerald-500' : tx.type === 'CHARGE' ? 'bg-rose-500/10 text-rose-500' : 'bg-indigo-500/10 text-indigo-500'}`}>{tx.type === 'PAYMENT' ? <ArrowUpRight size={20} /> : <ArrowDownRight size={20} />}</div>
                    <div><p className="font-black text-theme-main text-xs uppercase tracking-tight">{tx.description || tx.type}</p><p className="text-[9px] text-theme-muted font-bold mt-1 uppercase tracking-widest">{new Date(tx.createdAt).toLocaleString()}</p></div>
                  </div>
                  <div className="text-right">
                    <p className={`font-black text-sm ${tx.type === 'PAYMENT' ? 'text-emerald-500' : tx.type === 'CHARGE' ? 'text-rose-500' : 'text-theme-main'}`}>{tx.amount !== 0 ? (tx.type === 'PAYMENT' ? `+$${tx.amount.toFixed(2)}` : `-$${tx.amount.toFixed(2)}`) : ''}</p>
                    {tx.points !== 0 && <p className="text-[9px] font-black text-amber-500 uppercase tracking-widest">{tx.points > 0 ? `+${tx.points}` : tx.points} pts</p>}
                  </div>
                </div>
              ))}
              {history.length === 0 && <p className="text-center py-10 text-theme-muted font-bold italic text-xs uppercase">No transaction history found.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}